import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ArrowRight, Star, MapPin } from "lucide-react";
import { IMAGES } from "@/data/content";
import { whatsappLink } from "@/config/site";
import { track } from "@/lib/analytics";

const LINES = ["Professional Cleaning", "Services in Dublin"];

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section id="home" ref={ref} className="relative overflow-hidden bg-white pt-28 pb-16 sm:pt-32 lg:pt-36" data-testid="hero-section">
      <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:gap-12 lg:px-8">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-emerald-700"
          >
            <MapPin size={13} /> Dublin • Kildare • Surrounding Areas
          </motion.div>

          <h1 className="font-display text-5xl font-black leading-[1.02] tracking-tighter text-slate-900 sm:text-6xl lg:text-7xl">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span custom={i} variants={lineVariant} initial="hidden" animate="show" className="block">
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.5 }}
            className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600"
          >
            Residential and commercial cleaning for homes, apartments, offices, landlords and
            businesses across Dublin and surrounding areas.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.62 }}
            className="mt-8 flex flex-col gap-3 sm:flex-row"
          >
            <Link
              to="/contact" data-testid="hero-quote-btn"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-emerald-700 px-7 py-4 text-base font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-emerald-800 hover:shadow-xl hover:shadow-emerald-700/25"
            >
              Get a Free Quote
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href={whatsappLink()} onClick={() => track.whatsappClick("hero")}
              target="_blank" rel="noreferrer" data-testid="hero-whatsapp-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-slate-200 px-7 py-4 text-base font-bold text-slate-800 transition-all hover:border-[#25D366] hover:text-[#128C7E]"
            >
              <MessageCircle size={18} className="text-[#25D366]" /> WhatsApp Us
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.8 }}
            className="mt-8 text-sm font-semibold text-slate-500"
          >
            Clear pricing • Flexible appointments • Houses and apartments • Dublin and surrounding areas
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="relative aspect-[4/5] overflow-hidden rounded-[2rem] shadow-[0_30px_80px_rgba(4,120,87,0.18)] max-h-[70vh]">
            <motion.img
              src={IMAGES.hero} alt="Freshly cleaned, bright and empty modern living room ready for handover"
              style={{ y: imgY, scale: imgScale }}
              className="h-full w-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/25 to-transparent" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.9 }}
            className="absolute -bottom-5 -left-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-xl sm:-left-6"
          >
            <div className="flex items-center gap-1 text-emerald-600">
              {[...Array(5)].map((_, i) => <Star key={i} size={15} fill="currentColor" />)}
            </div>
            <p className="mt-1 text-sm font-bold text-slate-900">Inspection-ready standard</p>
            <p className="text-xs text-slate-500">Prepared for landlord & agent handover</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
